import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import ActionButton from "../components/ActionButton";

type Widget = {
  key: string;
  label: string;
  description: string;
  display_type: "number" | "list" | "bars" | "sparkline";
  params_schema: Record<string, string>;
};
type LayoutItem = { id: string; widget: string; params?: Record<string, any> };
type Dashboard = {
  id: number;
  user_id: number;
  name: string;
  layout: LayoutItem[];
  is_default: boolean;
};
type RenderedWidget = {
  id?: string;
  widget: string;
  ok: boolean;
  error?: string;
  display_type: string;
  data?: any;
};

const ts = () => Math.random().toString(36).slice(2, 10);

function NumberCard({ data, label }: { data: any; label?: string }) {
  const v = data?.value ?? "—";
  const unit = data?.unit || "";
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
      <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
        {v}
        {unit && <span className="text-base text-gray-400 font-normal ml-0.5">{unit}</span>}
      </div>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1">
        {data?.label || label}
      </div>
    </div>
  );
}

function ListCard({ data, label, widget }: { data: any; label?: string; widget: string }) {
  const items = data?.items || [];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
        {data?.label || label}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-gray-400">No data</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {items.map((it: any, i: number) => (
            <li key={i} className="flex items-center justify-between">
              <span className="text-gray-700 dark:text-gray-200 truncate pr-2">
                {widget === "recent_tickets" ? (
                  <Link to={`/tickets/${it.id}`} className="hover:underline">#{it.id} {it.title}</Link>
                ) : widget === "recent_alerts" ? (
                  <>{it.metric}{it.message ? ` — ${it.message}` : ""}</>
                ) : (
                  it.title || it.label || it.metric || JSON.stringify(it)
                )}
              </span>
              {it.priority && (
                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  {it.priority}
                </span>
              )}
              {it.severity && (
                <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                  it.severity === "critical" ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  : it.severity === "warning" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                }`}>{it.severity}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BarsCard({ data, label }: { data: any; label?: string }) {
  const items = data?.items || [];
  const max = Math.max(1, ...items.map((i: any) => i.value || 0));
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
        {data?.label || label}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-gray-400">No data</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-20 truncate text-gray-600 dark:text-gray-300">{it.label}</span>
              <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-700 rounded">
                <div
                  className="h-3 rounded bg-brand"
                  style={{ width: `${(it.value / max) * 100}%` }}
                />
              </div>
              <span className="w-6 text-right text-gray-700 dark:text-gray-200">{it.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SparklineCard({ data, label }: { data: any; label?: string }) {
  const series = data?.series || [];
  if (series.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{data?.label || label}</div>
        <div className="text-sm text-gray-400">No data</div>
      </div>
    );
  }
  const w = 240;
  const h = 60;
  const max = Math.max(1, ...series.map((p: any) => p.value));
  const step = w / Math.max(1, series.length - 1);
  const points = series
    .map((p: any, i: number) => `${i * step},${h - (p.value / max) * (h - 4) - 2}`)
    .join(" ");
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{data?.label || label}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16">
        <polyline fill="none" stroke="rgb(var(--brand-rgb))" strokeWidth={2} points={points} />
      </svg>
    </div>
  );
}

function WidgetCard({ rendered, widgets }: { rendered: RenderedWidget; widgets: Widget[] }) {
  if (!rendered.ok) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-800 dark:text-red-300">
        Widget {rendered.widget} failed: {rendered.error}
      </div>
    );
  }
  const meta = widgets.find((w) => w.key === rendered.widget);
  const label = meta?.label;
  switch (rendered.display_type) {
    case "number":
      return <NumberCard data={rendered.data} label={label} />;
    case "list":
      return <ListCard data={rendered.data} label={label} widget={rendered.widget} />;
    case "bars":
      return <BarsCard data={rendered.data} label={label} />;
    case "sparkline":
      return <SparklineCard data={rendered.data} label={label} />;
    default:
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 text-sm">
          <pre className="text-xs">{JSON.stringify(rendered.data, null, 2)}</pre>
        </div>
      );
  }
}

export default function CustomDashboards() {
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [layout, setLayout] = useState<LayoutItem[]>([]);
  const [rendered, setRendered] = useState<RenderedWidget[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const current = useMemo(
    () => dashboards.find((d) => d.id === currentId) || null,
    [dashboards, currentId]
  );

  const loadAll = async () => {
    try {
      const [w, d] = await Promise.all([
        api.get("/api/v1/dashboards/widgets"),
        api.get("/api/v1/dashboards"),
      ]);
      setWidgets(w.data?.widgets || []);
      const list: Dashboard[] = d.data || [];
      setDashboards(list);
      if (list.length === 0) {
        // Auto-create a default dashboard with a sensible starter set.
        const r = await api.post("/api/v1/dashboards", {
          name: "My dashboard",
          is_default: true,
          layout: [
            { id: ts(), widget: "open_critical_alerts" },
            { id: ts(), widget: "devices_offline" },
            { id: ts(), widget: "open_tickets" },
            { id: ts(), widget: "ai_actions_pending" },
            { id: ts(), widget: "discovered_unmanaged" },
            { id: ts(), widget: "patch_compliance_pct" },
            { id: ts(), widget: "alert_trend_30d" },
            { id: ts(), widget: "recent_tickets", params: { limit: 5 } },
            { id: ts(), widget: "recent_alerts", params: { limit: 5 } },
            { id: ts(), widget: "tickets_by_priority" },
          ],
        });
        setDashboards([r.data]);
        setCurrentId(r.data.id);
        setLayout(r.data.layout || []);
        await renderData(r.data.id, r.data.layout || []);
        return;
      }
      const def = list.find((x) => x.is_default) || list[0];
      setCurrentId(def.id);
      setLayout(def.layout || []);
      await renderData(def.id, def.layout || []);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const renderData = async (id: number, lay: LayoutItem[]) => {
    try {
      const r = await api.post(`/api/v1/dashboards/${id}/data`, { layout: lay });
      setRendered(r.data?.widgets || []);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadAll();
    // Run-once initializer on mount; loadAll closes over no changing inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentId == null) return;
    const d = dashboards.find((x) => x.id === currentId);
    if (!d) return;
    setLayout(d.layout || []);
    renderData(d.id, d.layout || []);
    // Intentionally keyed on the selected dashboard id only; `dashboards` is
    // read as a lookup at that moment, not a trigger (it's already current by
    // the time currentId points at it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const addWidget = async (key: string) => {
    const next = [...layout, { id: ts(), widget: key }];
    setLayout(next);
    setShowPicker(false);
    if (currentId != null) await renderData(currentId, next);
  };

  const removeWidget = async (id: string) => {
    const next = layout.filter((w) => w.id !== id);
    setLayout(next);
    if (currentId != null) await renderData(currentId, next);
  };

  const moveWidget = async (id: string, dir: -1 | 1) => {
    const idx = layout.findIndex((w) => w.id === id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[idx], next[j]] = [next[j], next[idx]];
    setLayout(next);
    if (currentId != null) await renderData(currentId, next);
  };

  const saveLayout = async () => {
    if (currentId == null) return;
    setBusy(true);
    try {
      const r = await api.put(`/api/v1/dashboards/${currentId}`, { layout });
      setDashboards(dashboards.map((d) => (d.id === currentId ? r.data : d)));
      setEditMode(false);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const renameDashboard = async () => {
    if (!current) return;
    const name = window.prompt("Rename dashboard:", current.name);
    if (!name) return;
    try {
      const r = await api.put(`/api/v1/dashboards/${current.id}`, { name });
      setDashboards(dashboards.map((d) => (d.id === current.id ? r.data : d)));
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const newDashboard = async () => {
    const name = window.prompt("New dashboard name:") || "";
    if (!name) return;
    try {
      const r = await api.post("/api/v1/dashboards", { name, layout: [] });
      const next = [...dashboards, r.data];
      setDashboards(next);
      setCurrentId(r.data.id);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const deleteDashboard = async () => {
    if (!current) return;
    if (!window.confirm(`Delete dashboard "${current.name}"?`)) return;
    try {
      await api.delete(`/api/v1/dashboards/${current.id}`);
      const next = dashboards.filter((d) => d.id !== current.id);
      setDashboards(next);
      setCurrentId(next[0]?.id ?? null);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const availableToAdd = useMemo(
    () => widgets.filter((w) => !layout.some((l) => l.widget === w.key)),
    [widgets, layout]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Custom Dashboards</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Build your own at-a-glance view. Each user gets their own dashboards.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={currentId ?? ""}
            onChange={(e) => setCurrentId(Number(e.target.value))}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            {dashboards.map((d) => (
              <option key={d.id} value={d.id}>{d.name}{d.is_default ? " ★" : ""}</option>
            ))}
          </select>
          <ActionButton onAction={() => newDashboard()} variant="raw" className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded" pendingLabel="Working…">New</ActionButton>
          <button onClick={renameDashboard} disabled={!current} className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded">Rename</button>
          <button onClick={deleteDashboard} disabled={!current} className="text-sm px-2 py-1 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded">Delete</button>
          <button
            onClick={() => setEditMode((m) => !m)}
            disabled={!current}
            className={`text-sm px-3 py-1 rounded ${editMode ? "bg-brand text-white" : "border border-gray-300 dark:border-gray-600"}`}
          >
            {editMode ? "Done editing" : "Edit"}
          </button>
          {editMode && (
            <button onClick={saveLayout} disabled={busy} className="text-sm px-3 py-1 bg-brand text-white rounded">
              Save layout
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded text-sm">{err}</div>
      )}

      {editMode && (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center gap-2">
          <button onClick={() => setShowPicker(!showPicker)} className="px-3 py-1.5 bg-brand text-white rounded text-sm">
            + Add widget
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Use the arrows on each card to reorder. Don't forget to "Save layout".
          </span>
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowPicker(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-2xl w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Add widget</h3>
            {availableToAdd.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                All widgets are already on this dashboard. Remove one first to free up the slot.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableToAdd.map((w) => (
                  <button
                    key={w.key}
                    onClick={() => addWidget(w.key)}
                    className="text-left p-3 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="font-medium text-gray-800 dark:text-gray-100">{w.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{w.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rendered.map((rw) => {
          const layoutEntry = layout.find((l) => l.id === rw.id);
          return (
            <div key={rw.id} className="relative">
              <WidgetCard rendered={rw} widgets={widgets} />
              {editMode && layoutEntry && (
                <div className="absolute top-1 right-1 flex gap-1 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700 p-0.5">
                  <button onClick={() => moveWidget(layoutEntry.id, -1)} className="text-xs px-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">↑</button>
                  <button onClick={() => moveWidget(layoutEntry.id, 1)} className="text-xs px-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">↓</button>
                  <button onClick={() => removeWidget(layoutEntry.id)} className="text-xs px-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">✕</button>
                </div>
              )}
            </div>
          );
        })}
        {rendered.length === 0 && current && (
          <div className="col-span-full text-sm text-gray-500 dark:text-gray-400">
            This dashboard is empty. Click <strong>Edit</strong> → <strong>Add widget</strong> to start.
          </div>
        )}
      </div>
    </div>
  );
}
